import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Star, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { QueryKey } from '@/Config/api/queryKey';
import { Button } from '@/Domain/components/Shared/Button/Button';
import { AIAssistantsData } from '@/Domain/components/TopBar/types';
import { PostHogEventEnum } from '@/Domain/enum/PostHogEvent.enum';
import { usePostHogAnalytics } from '@/Domain/hooks/App/usePostHog/usePostHogAnalytics.hook';

const SHOPIFY_REVIEW_URL = 'https://apps.shopify.com/quickchat-ai#modal-show=WriteReviewModal';

export const ShopifyReviewPrompt = () => {
  const { t } = useTranslation('review_prompt');
  const queryClient = useQueryClient();
  const { capture } = usePostHogAnalytics();

  const { data: aiAssistants } = useQuery<AIAssistantsData>({
    queryKey: [QueryKey.aiAssistants.get],
  });

  const current = aiAssistants?.current_ai_assistant;
  const isEligible = current?.review_prompt_eligible === true;

  const [hiddenScenarios, setHiddenScenarios] = useState<Set<string>>(() => new Set());

  const dismissMutation = useMutation<{ dismissed: boolean }, Error, { scenario_id: string }>({
    mutationKey: [QueryKey.aiAssistants.dismissReviewPrompt],
    onSuccess: (_data, { scenario_id }) => {
      queryClient.setQueryData<AIAssistantsData>([QueryKey.aiAssistants.get], (prev) =>
        prev?.current_ai_assistant?.scenario_id === scenario_id
          ? {
              ...prev,
              current_ai_assistant: { ...prev.current_ai_assistant, review_prompt_eligible: false },
            }
          : prev,
      );
    },
  });

  const shownScenariosRef = useRef(new Set<string>());
  useEffect(() => {
    const scenarioId = current?.scenario_id;
    if (!isEligible || !scenarioId || shownScenariosRef.current.has(scenarioId)) return;
    shownScenariosRef.current.add(scenarioId);
    capture(PostHogEventEnum.ShopifyReviewPromptShown);
  }, [current?.scenario_id, isEligible, capture]);

  const dismiss = () => {
    if (!current) return;
    dismissMutation.mutate({ scenario_id: current.scenario_id });
    setHiddenScenarios((previous) => new Set(previous).add(current.scenario_id));
  };

  const handleReview = () => {
    capture(PostHogEventEnum.ShopifyReviewPromptClicked);
    window.open(SHOPIFY_REVIEW_URL, '_blank', 'noopener,noreferrer');
    dismiss();
  };

  const handleLater = () => {
    capture(PostHogEventEnum.ShopifyReviewPromptDismissed);
    dismiss();
  };

  if (!isEligible || !current || hiddenScenarios.has(current.scenario_id)) return null;

  return (
    <div
      className="flex items-center gap-3 border-b border-border bg-card px-4 py-2.5"
      data-testid="shopify-review-prompt"
    >
      <span className="inline-flex size-7 shrink-0 items-center justify-center rounded-lg bg-warning-muted">
        <Star className="size-4 text-warning" />
      </span>

      <div className="flex min-w-0 flex-1 items-baseline gap-2">
        <span className="min-w-0 truncate text-sm font-medium text-foreground">{t('title')}</span>
        <span className="hidden min-w-0 truncate text-sm text-muted-foreground lg:inline">
          {t('description')}
        </span>
      </div>

      <Button
        variant="dark"
        size="sm"
        className="shrink-0"
        onClick={handleReview}
        data-testid="shopify-review-prompt-review"
      >
        {t('review_cta')}
      </Button>
      <Button
        variant="ghost"
        size="icon"
        aria-label={t('dismiss')}
        className="shrink-0"
        onClick={handleLater}
        data-testid="shopify-review-prompt-later"
      >
        <X className="size-4" />
      </Button>
    </div>
  );
};
